import { DataGrid } from '@mui/x-data-grid'
import { Box, IconButton, Button, Tooltip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import TextField from '@mui/material/TextField'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { getAllUsers } from '../../graphql/queries'
import { useState, useEffect, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

const columns = [
  {
    field: 'tableIndex',
    headerName: '#',
    filterable: false,
    minWidth: 120,
    maxWidth: 170,
    flex: 1,
    sortable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
        {params.value}
        <DetailPage id={params.row.id} />
      </Box>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    sortable: false,
    minWidth: 120,
    flex: 1,
    valueGetter: params => new Date(params.row.createdAt).toLocaleDateString('tr-TR'),
  },
  { field: 'role', headerName: 'Role', sortable: false, minWidth: 120, flex: 1 },
  {
    field: 'rcUserId',
    headerName: 'Revenuecat Id',
    sortable: false,
    minWidth: 220,
    flex: 1,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {params.value}
        <CopyCell value={params.value} />
      </Box>
    ),
  },
  { field: 'store', headerName: 'Store', sortable: false, minWidth: 120, flex: 1, maxWidth: 170 },
]

export function DetailPage({ id }) {
  const navigate = useNavigate()

  const handleNavigate = async () => {
    navigate(`/users/${id}`)
  }
  return (
    <Tooltip title="View User Details">
      <Button
        onClick={handleNavigate}
        variant="outlined"
        className="actionBtn"
        sx={{ fontSize: {xs:'0.7rem', md:'small'} }}
      >
        Details
      </Button>
    </Tooltip>
  )
}

export function CopyCell({ value }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy}>
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  )
}

export default function UserTable() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialPage = parseInt(searchParams.get('page')) || 0
  const initialPageSize = parseInt(searchParams.get('pageSize')) || 50
  const initialSearch = searchParams.get('search') || ''
  const [page, setPage] = useState(initialPage)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [search, setSearch] = useState(initialSearch)

  const [storeOption, setStoreOption] = useState('')
  const wrapRef = useRef(null)

  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
    })
  }, [page, pageSize, search])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1000 && wrapRef.current) {
        wrapRef.current.scrollLeft = 0
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const { loading, error, data } = useQuery(getAllUsers, {
    variables: {
      page: page + 1,
      pageSize,
      rcUserId: search || null,
      store: storeOption ? storeOption : null,
    },
    fetchPolicy: 'cache-and-network',
  })

  if (error) {
    return <p>Error: {error.message}</p>
  }

  const rowsData = data?.getAllUsers?.users ?? []
  const totalCount = data?.getAllUsers?.totalCount ?? 0

  const processedRows = rowsData.map((row, index) => ({
    ...row,
    tableIndex: page * pageSize + index + 1,
  }))

  const handleSearchChange = event => {
    setSearch(event.target.value)
    setPage(0)
  }

  const handlePaginationModelChange = newModel => {
    setPage(newModel.page)
    setPageSize(newModel.pageSize)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{ alignItems: 'flex-start', display: 'flex', gap: 3, mb: 3, alignItems: 'flex-end' }}
      >
        <TextField
          id="standard-basic"
          name="rcUserId"
          label="RevenueCat User ID"
          variant="standard"
          value={search}
          onChange={handleSearchChange}
          sx={{ width: '250px' }}
        />
        {search && (
          <IconButton onClick={() => setSearch('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <DataGrid
        disableColumnMenu
        ref={wrapRef}
        rows={processedRows}
        columns={columns}
        rowHeight={80}
        paginationMode="server"
        rowCount={totalCount}
        loading={loading}
        pagination
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          height: 680,
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto',
            overflowX: 'auto',
          },
          fontSize: '13px',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell': {
            whiteSpace: 'break-spaces',
          },
          '.MuiFormControl-root': {
            margin: '0px 0px 10px 0px',
            width: '100px',
          },
          '.MuiOutlinedInput-root': {
            width: '150px',
          },
          '.MuiToolbar-regular': {
            padding: 0,
          },
          '& .actionBtn': {
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity .2s',
          },
          '& .MuiDataGrid-row:hover .actionBtn': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
      />
    </Box>
  )
}
